/* eslint-disable react/prop-types */
import React, {useEffect, useMemo, useState} from 'react';
import {getTemplateCategoryNames, wizardTemplateRules} from '../../../../utility/templateWizardRules';
import {
    Box,
    Button,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import {useGetFormTemplatesQuery} from '../../../../api/services/formsService';
import {useGetCurrentSchoolQuery} from '../../../../api/services/schoolManagementService';
import CreateFormTemplatePreviewDialog from './CreateFormTemplatePreviewDialog';
import {getTemplateHeaderColour} from './FormTemplatePreviewVisual';
import FORM_AUDIENCE_TYPE from '../../../../constants/FormAudienceType';
import FORM_TYPE from '../../../../constants/FormType';

const ALL_CATEGORIES_VALUE = 'all-categories';
const ALL_SUB_CATEGORIES_VALUE = 'all-sub-categories';

const normaliseText = (value) => String(value || '').trim().toLowerCase();

const isInactiveCategoryValue = (value) => normaliseText(value) === 'inactive';

const getTemplateDisplayName = (template) => template?.name || template?.formName || '';

const getNormalisedTemplateMeta = (template) => ({
    category: normaliseText(template?.category),
    parentCategory: normaliseText(template?.parentCategory),
    type: normaliseText(template?.type),
});

// AD-2689: every self-service template is also listed under Self Service, by its own category.
const SELF_SERVICE_CATEGORY = 'Self Service';
const placesOf = (template, withCopies = true) => {
    const own = getTemplateCategoryNames(template);
    const places = [own];
    // A template is a self-service one when it is saved as one, or when it sits in a 'Self Service' sub-category.
    const isSelfServiceTemplate = normaliseText(template?.type) === 'selfservice' ||
        normaliseText(own.subCategory) === normaliseText(SELF_SERVICE_CATEGORY);
    if (withCopies && isSelfServiceTemplate && normaliseText(own.category) !== normaliseText(SELF_SERVICE_CATEGORY)) {
        places.push({category: SELF_SERVICE_CATEGORY, subCategory: own.category || own.subCategory});
    }
    return places;
};

const isBlankTemplate = (template) => getNormalisedTemplateMeta(template).category === 'blank';

const CreateFormTemplatePreviewList = ({
                                            onTemplateSelected,
                                            recipientAssignmentType,
                                            triggerRecipientAssignmentType,
                                            audience,
                                            linkToStudents,
                                            linkToEmployees,
                                            parentAccountType,
                                            formType,
                                            templateSource,
    height = '75vh',
}) => {
    const {data: templates = []} = useGetFormTemplatesQuery();
    // AD-2689: a school with 'Hide Boarder Groups' on has no boarders, so the Boarding templates are not listed.
    const {data: currentSchool} = useGetCurrentSchoolQuery({});
    const isBoardingTemplate = (template) => {
        const {category, subCategory} = getTemplateCategoryNames(template);
        return normaliseText(category) === 'boarding' || normaliseText(subCategory) === 'boarding';
    };
    const [previewTemplate, setPreviewTemplate] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState(ALL_CATEGORIES_VALUE);
    const [selectedSubCategory, setSelectedSubCategory] = useState(ALL_SUB_CATEGORIES_VALUE);
    const previewEnabled = true;
    const isSchoolSource = templateSource === 'school';
    const isTriggerForm = formType === FORM_TYPE.triggerForm;

    const toValidAssignmentType = (v) => {
        if (v == null || v === '') return null;
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
    };
    const recipientAssignmentTypeNum = toValidAssignmentType(recipientAssignmentType);
    const triggerRecipientAssignmentTypeNum = toValidAssignmentType(triggerRecipientAssignmentType);

    const effectiveRecipientAssignmentTypeNum =
        triggerRecipientAssignmentTypeNum ?? recipientAssignmentTypeNum ?? null;

    const recipientTypeAudience = (() => {
        if (audience !== FORM_AUDIENCE_TYPE.externalUsers) return audience;

        return FORM_AUDIENCE_TYPE.externalUsers;
    })();

    const filterBySource = (t) => isSchoolSource ? !!t.schoolId : !t.schoolId;

    const filterSchoolTemplateByFormType = (t) => {
        if (!formType) return true;

        if (formType === 'shareable') {
            return t.type === 'shareable';
        }

        if (formType === 'application') {
            return t.type === 'application';
        }

        if (formType === 'email' || formType === 'selfService' || formType === 'noEmail') {
            const blockedTypes = ['application', 'shareable'];
            return !blockedTypes.includes(t.type);
        }

        return true;
    };

    const {filterAdmicityTemplateByFormType, matchesTemplateAudience} = wizardTemplateRules({
        formType,
        linkToStudents,
        linkToEmployees,
        parentAccountType,
        recipientTypeAudience,
        effectiveRecipientAssignmentTypeNum
    });

    const hasActiveCategoryData = (t) => {
        const {
            category,
            subCategory
        } = getTemplateCategoryNames(t);

        return (category || subCategory) &&
            !isInactiveCategoryValue(category) &&
            !isInactiveCategoryValue(subCategory);
    };

    const baseFilteredTemplates = useMemo(() => {
        return templates
            .filter(filterBySource)
            .filter(t => !(currentSchool?.hideBoarderGroups && isBoardingTemplate(t)))
            .filter(t => {
                // Admicity blank templates are no longer offered here - the wizard's
                // "Blank Form" creation method covers them. School templates saved from
                // a blank form keep the listing behaviour they already had.
                if (isBlankTemplate(t)) return isSchoolSource;

                // A trigger form takes any type of template, but only one made for its audience (AD-2689).
                if (isTriggerForm) return matchesTemplateAudience(t);

                const passesFormType = isSchoolSource
                    ? filterSchoolTemplateByFormType(t)
                    : filterAdmicityTemplateByFormType(t);

                if (!passesFormType) return false;

                return matchesTemplateAudience(t);
            })
            .filter(hasActiveCategoryData);
    }, [
        templates,
        isTriggerForm,
        isSchoolSource,
        formType,
        audience,
        parentAccountType,
        recipientTypeAudience,
        effectiveRecipientAssignmentTypeNum,
        currentSchool?.hideBoarderGroups,
    ]);

    const categoryOptions = useMemo(() => {
        return Array.from(
            new Set(
                baseFilteredTemplates
                    .flatMap(t => placesOf(t).map(place => place.category))
                    .filter(Boolean)
                    .filter(category => !isInactiveCategoryValue(category))
            )
        ).sort((a, b) => a.localeCompare(b));
    }, [baseFilteredTemplates]);

    const subCategoryOptions = useMemo(() => {
        const available = baseFilteredTemplates;

        return Array.from(
            new Set(
                available
                    .flatMap(t => placesOf(t))
                    .filter(place => selectedCategory === ALL_CATEGORIES_VALUE ||
                        normaliseText(place.category) === normaliseText(selectedCategory))
                    .map(place => place.subCategory)
                    .filter(Boolean)
                    .filter(subCategory => !isInactiveCategoryValue(subCategory))
            )
        ).sort((a, b) => a.localeCompare(b));
    }, [baseFilteredTemplates, selectedCategory]);

    useEffect(() => {
        if (
            selectedCategory !== ALL_CATEGORIES_VALUE &&
            !categoryOptions.some(category => normaliseText(category) === normaliseText(selectedCategory))
        ) {
            setSelectedCategory(ALL_CATEGORIES_VALUE);
        }
    }, [categoryOptions, selectedCategory]);

    useEffect(() => {
        if (
            selectedSubCategory !== ALL_SUB_CATEGORIES_VALUE &&
            !subCategoryOptions.some(subCategory => normaliseText(subCategory) === normaliseText(selectedSubCategory))
        ) {
            setSelectedSubCategory(ALL_SUB_CATEGORIES_VALUE);
        }
    }, [subCategoryOptions, selectedSubCategory]);

    const searchedAndFilteredTemplates = useMemo(() => {
        const searchValue = normaliseText(searchTerm);

        return baseFilteredTemplates.filter((template) => {
            const {
                category,
                subCategory
            } = getTemplateCategoryNames(template);
            const templateName = getTemplateDisplayName(template);

            const matchesSearch = !searchValue ||
                normaliseText(templateName).includes(searchValue) ||
                normaliseText(subCategory).includes(searchValue) ||
                normaliseText(category).includes(searchValue) ||
                normaliseText(template.type).includes(searchValue);

            if (!matchesSearch) return false;

            // AD-2689: a search looks in every category.
            if (searchValue) return true;

            return placesOf(template).some(place =>
                (selectedCategory === ALL_CATEGORIES_VALUE ||
                    normaliseText(place.category) === normaliseText(selectedCategory)) &&
                (selectedSubCategory === ALL_SUB_CATEGORIES_VALUE ||
                    normaliseText(place.subCategory) === normaliseText(selectedSubCategory)));
        });
    }, [baseFilteredTemplates, searchTerm, selectedCategory, selectedSubCategory]);

    const groupTemplates = (list, withCopies = true) => {
        const groupsMap = new Map();

        list.forEach((template) => placesOf(template, withCopies).forEach(({category, subCategory}) => {
            if (!subCategory || isInactiveCategoryValue(subCategory) || isInactiveCategoryValue(category)) {
                return;
            }

            const sectionKey = category || subCategory;

            if (!groupsMap.has(sectionKey)) {
                groupsMap.set(sectionKey, {
                    categoryName: category,
                    subCategoriesMap: new Map(),
                });
            }

            const categoryGroup = groupsMap.get(sectionKey);

            if (!categoryGroup.subCategoriesMap.has(subCategory)) {
                categoryGroup.subCategoriesMap.set(subCategory, []);
            }

            categoryGroup.subCategoriesMap.get(subCategory).push(template);
        }));

        return Array.from(groupsMap.values())
            .map((group) => {
                const subCategories = Array.from(group.subCategoriesMap.entries())
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([subCategoryName, templatesForSubCategory]) => ({
                        subCategoryName,
                        templates: templatesForSubCategory.sort((a, b) =>
                            getTemplateDisplayName(a).localeCompare(getTemplateDisplayName(b))
                        ),
                    }));

                const totalCount = subCategories.reduce((sum, subCategory) => sum + subCategory.templates.length, 0);

                return {
                    categoryName: group.categoryName || subCategories[0]?.subCategoryName || '',
                    subCategories,
                    totalCount,
                };
            })
            .filter(group => group.categoryName)
            .filter(group => !isInactiveCategoryValue(group.categoryName))
            .filter(group => group.subCategories.length > 0)
            .sort((a, b) => a.categoryName.localeCompare(b.categoryName));
    };

    // A search lists each template once; a chosen category or sub-category shows only its own templates.
    const groupedTemplates = useMemo(() => {
        const searching = !!normaliseText(searchTerm);
        return groupTemplates(searchedAndFilteredTemplates, !searching)
            .filter(group => searching || selectedCategory === ALL_CATEGORIES_VALUE ||
                normaliseText(group.categoryName) === normaliseText(selectedCategory))
            .map(group => ({
                ...group,
                subCategories: group.subCategories.filter(({subCategoryName}) =>
                    searching || selectedSubCategory === ALL_SUB_CATEGORIES_VALUE ||
                    normaliseText(subCategoryName) === normaliseText(selectedSubCategory)),
            }));
    }, [searchedAndFilteredTemplates, searchTerm, selectedCategory, selectedSubCategory]); // eslint-disable-line
    const sidebarGroups = useMemo(() => groupTemplates(baseFilteredTemplates), [baseFilteredTemplates]);

    const emptyMessage = isSchoolSource
        ? 'No school templates exist that match this form type, audience, or filter yet.'
        : 'No Admicity templates available for the current filter.';

    const handleSelectTemplate = (template) => {
        onTemplateSelected?.(template);
    };

    // AD-2689: a sidebar of categories, a search box and a list of template names.
    const isSearching = !!normaliseText(searchTerm);
    // AD-2689: the window opens on Admissions (or the first category), showing all its templates.
    const isLanding = false;
    const defaultCategory = (sidebarGroups.find(group => normaliseText(group.categoryName) === 'admissions') || sidebarGroups[0])?.categoryName;
    useEffect(() => {
        if (selectedCategory === ALL_CATEGORIES_VALUE && defaultCategory) {
            setSelectedCategory(defaultCategory);
            setSelectedSubCategory(ALL_SUB_CATEGORIES_VALUE);
        }
    }, [selectedCategory, defaultCategory]);
    const chooseCategory = (category, subCategory = ALL_SUB_CATEGORIES_VALUE) => {
        setSearchTerm('');
        setSelectedCategory(category);
        setSelectedSubCategory(subCategory);
    };
    const isSelected = (category, subCategory = ALL_SUB_CATEGORIES_VALUE) =>
        !isSearching &&
        normaliseText(selectedCategory) === normaliseText(category) &&
        normaliseText(selectedSubCategory) === normaliseText(subCategory);
    const colourOf = (category) => getTemplateHeaderColour({category, parentCategory: category});
    // AD-2689: each category is a cell filled with its colour; a chosen sub-category is shaded in that colour.
    const sidebarItemSx = (selected, indent = 0, colour = '#186BBE') => (indent
        ? {
            display: 'block',
            width: '100%',
            textAlign: 'left',
            pl: 3,
            pr: 2,
            py: 0.25,
            border: 0,
            backgroundColor: 'transparent',
            color: 'text.primary',
            fontWeight: selected ? 700 : 400,
            fontSize: 13,
            cursor: 'pointer',
            fontFamily: 'inherit',
            '&:hover': {textDecoration: 'underline'},
        }
        : {
            display: 'block',
            width: '100%',
            textAlign: 'left',
            px: 2,
            py: 0.5,
            border: 0,
            borderTop: '1px solid #fff',
            backgroundColor: colour,
            color: '#fff',
            fontWeight: 600,
            fontSize: 14,
            cursor: 'pointer',
            fontFamily: 'inherit',
            '&:hover': {filter: 'brightness(0.92)'},
        });

    // AD-2689: a fine line in the category's colour above and below each template.
    const rowColour = (template) => colourOf(isSearching ? getTemplateCategoryNames(template).category : selectedCategory);
    const listedTemplates = groupedTemplates.flatMap(group =>
        group.subCategories.flatMap(({templates: list}) => list)
    ).filter((template, index, all) => all.findIndex(other => other.id === template.id) === index)
        // AD-2689: one list in alphabetical order.
        .sort((a, b) => getTemplateDisplayName(a).localeCompare(getTemplateDisplayName(b)));
    const lastRowIndex = listedTemplates.length - 1;
    const templateRow = (template, index) => (
        <Stack
            key={template.id}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
            data-testid={`template-row-${template.id}`}
            sx={{px: 1.5, py: 0.5, backgroundColor: '#fff', borderTop: `1px solid ${rowColour(template)}`, ...(index === lastRowIndex ? {borderBottom: `1px solid ${rowColour(template)}`} : {})}}
        >
            <Typography variant="body2" sx={{cursor: 'pointer'}} onClick={() => handleSelectTemplate(template)}>
                {getTemplateDisplayName(template)}
            </Typography>
            <Stack direction="row" spacing={1} sx={{flexShrink: 0}}>
                {previewEnabled && (
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setPreviewTemplate(template)}
                        data-testid={`template-preview-${template.id}`}
                        sx={{
                            minWidth: 0,
                            px: 1,
                            py: 0,
                            lineHeight: 1.7,
                            fontSize: 11,
                            whiteSpace: 'nowrap',
                            backgroundColor: '#fff',
                            color: rowColour(template),
                            borderColor: rowColour(template),
                            '&:hover': {backgroundColor: '#fff', borderColor: rowColour(template)},
                        }}
                    >
                        Preview
                    </Button>
                )}
                <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleSelectTemplate(template)}
                    sx={{
                        minWidth: 0,
                        px: 1,
                        py: 0,
                        lineHeight: 1.7,
                        fontSize: 11,
                        whiteSpace: 'nowrap',
                        backgroundColor: '#fff',
                        color: rowColour(template),
                        borderColor: rowColour(template),
                        '&:hover': {backgroundColor: '#fff', borderColor: rowColour(template)},
                    }}
                >
                    Use
                </Button>
            </Stack>
        </Stack>
    );

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', height, bgcolor: '#fff', border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden'}}>
            <Box sx={{px: 1.5, py: 0.75, borderBottom: '1px solid #e0e0e0'}}>
                <TextField
                    fullWidth
                    size="small"
                    label="Search templates"
                    autoComplete="off"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                />
            </Box>
            <Box sx={{display: 'flex', flex: 1, minHeight: 0}}>
                <Box
                    component="nav"
                    data-testid="template-categories"
                    sx={{width: 210, flexShrink: 0, overflowY: 'auto', borderRight: '1px solid #e0e0e0', py: 0.25}}
                >
                    {sidebarGroups.map(group => (
                        <Box key={group.categoryName}>
                            <Box
                                component="button"
                                sx={sidebarItemSx(!isSearching && normaliseText(selectedCategory) === normaliseText(group.categoryName), 0, colourOf(group.categoryName))}
                                onClick={() => chooseCategory(group.categoryName)}
                            >
                                {group.categoryName}
                            </Box>
                            {!isSearching && normaliseText(selectedCategory) === normaliseText(group.categoryName) && (
                                <Box
                                    component="button"
                                    sx={sidebarItemSx(isSelected(group.categoryName), 2, colourOf(group.categoryName))}
                                    onClick={() => chooseCategory(group.categoryName)}
                                >
                                    All
                                </Box>
                            )}
                            {!isSearching && normaliseText(selectedCategory) === normaliseText(group.categoryName) &&
                                group.subCategories.map(({subCategoryName, templates: list}) => (
                                    <Box
                                        key={subCategoryName}
                                        component="button"
                                        sx={sidebarItemSx(isSelected(group.categoryName, subCategoryName), 2, colourOf(group.categoryName))}
                                        onClick={() => chooseCategory(group.categoryName, subCategoryName)}
                                    >
                                        {subCategoryName}
                                    </Box>
                                ))}
                        </Box>
                    ))}
                </Box>

                <Box sx={{flex: 1, overflowY: 'auto', px: 1, py: 0.25}} data-testid="template-list">
                    {!isLanding && searchedAndFilteredTemplates.length === 0 && (
                        <Typography color="text.secondary" textAlign="center" mt={6}>
                            {emptyMessage}
                        </Typography>
                    )}

                    {/* AD-2689: one list of names, without headings, so more templates are in view. */}
                    {!isLanding && listedTemplates.map(templateRow)}
                </Box>
            </Box>

            {previewEnabled && (
                <CreateFormTemplatePreviewDialog
                    open={!!previewTemplate}
                    template={previewTemplate}
                    colour={previewTemplate ? rowColour(previewTemplate) : undefined}
                    onClose={() => setPreviewTemplate(null)}
                    onUseTemplate={(template) => {
                        handleSelectTemplate(template);
                        setPreviewTemplate(null);
                    }}
                />
            )}
        </Box>
    );
};

export default CreateFormTemplatePreviewList;
